import { useState } from 'react';
import { Card, Typography, Upload, Button, Space, message, Popconfirm, Tooltip } from 'antd';
import { BgColorsOutlined, ReloadOutlined, DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import { useNodeSettingsAdmin } from '../../hooks/use_node_settings_admin';
import { useNodeInfo } from '../../hooks/use_node_info';
import { NodeAvatar } from '../Node/NodeAvatar';
import { nodeAvatarSeed, randomSeed, randomPalette } from '../../utils/entity_avatar';
// Shared with entity pictures — see utils/image_upload.js for the limits.
import { ACCEPTED_IMAGE_TYPES, MAX_IMAGE_DIMENSION, precheckImage } from '../../utils/image_upload';

const { Title, Text } = Typography;

/**
 * Superadmin-only (behind feature_branding): the node's mark, shown in the
 * navbar, the public header and the browser tab. By default it is the
 * node's generated avatar, which can be reshuffled here — colours, shape —
 * exactly like an entity's. Or an image can be uploaded instead (raster
 * only, no SVG — see routers/node_settings.py for why); removing it goes
 * back to the generated one. Saving invalidates the cached public
 * node-settings value so NavBar.jsx / PublicHeader.jsx / DocumentTitle.jsx
 * pick it up immediately.
 */
function NodeBranding() {
    const { logoUrl, avatar, loading, uploadLogo, removeLogo, updateAvatar } = useNodeSettingsAdmin();
    const { nodeId, nodeName } = useNodeInfo();
    const nodeKey = nodeId || nodeName || null;
    const [busy, setBusy] = useState(false);

    const run = (promise, okText) => {
        setBusy(true);
        return promise
            .then(() => { if (okText) message.success(okText); })
            .catch(err => message.error(err?.message || 'Could not update the logo'))
            .finally(() => setBusy(false));
    };

    // Keep whichever half wasn't touched, so shuffling colours doesn't also
    // silently change the shape and vice versa (same rule as entity avatars).
    const shuffleColors = () => run(updateAvatar({ seed: avatar?.seed || nodeAvatarSeed(nodeKey), colors: randomPalette() }));
    const shuffleShape = () => run(updateAvatar({ seed: randomSeed(), colors: avatar?.colors || randomPalette() }));

    const handleUpload = file => {
        run(precheckImage(file, 'Logo').then(() => uploadLogo(file)), 'Logo updated');
        return false; // stop antd's own auto-upload; we POST manually above
    };
    const handleRemove = () => run(removeLogo(), 'Back to the generated logo');

    return (
        <Card style={{ marginTop: 16 }}>
            <Title level={5} style={{ marginTop: 0 }}>Logo</Title>
            <Text type="secondary">
                Shown in the navbar and the browser tab, on every page including before sign-in.
                Your node starts with a generated mark you can reshuffle, or you can upload an image:
                PNG, JPEG, or WEBP, under 2MB and at most {MAX_IMAGE_DIMENSION}x{MAX_IMAGE_DIMENSION} pixels.
                It renders 20px tall, so a wide wordmark at 2x or 3x works best.
            </Text>

            {!loading && (
                <Space size={16} align="center" style={{ marginTop: 16, flexWrap: 'wrap' }}>
                    {logoUrl
                        ? <img src={logoUrl} alt="Current logo" style={{ height: 40, maxWidth: 240, objectFit: 'contain' }} />
                        : <NodeAvatar serverId={nodeKey} avatar={avatar} size={64} />}

                    <Space size={4}>
                        {!logoUrl && (
                            <Tooltip title="Shuffle colours">
                                <Button shape="circle" icon={<BgColorsOutlined />} onClick={shuffleColors} disabled={busy} aria-label="Shuffle colours" />
                            </Tooltip>
                        )}
                        {!logoUrl && (
                            <Tooltip title="Shuffle shape">
                                <Button shape="circle" icon={<ReloadOutlined />} onClick={shuffleShape} disabled={busy} aria-label="Shuffle shape" />
                            </Tooltip>
                        )}
                        <Upload accept={ACCEPTED_IMAGE_TYPES.join(',')} beforeUpload={handleUpload} showUploadList={false} disabled={busy}>
                            <Button icon={<UploadOutlined />} loading={busy}>{logoUrl ? 'Replace image' : 'Upload an image'}</Button>
                        </Upload>
                        {logoUrl && (
                            <Popconfirm
                                title="Remove the uploaded logo?"
                                description="The navbar goes back to the generated mark."
                                okText="Remove"
                                okButtonProps={{ danger: true }}
                                onConfirm={handleRemove}
                            >
                                <Button type="text" danger icon={<DeleteOutlined />} disabled={busy}>Remove</Button>
                            </Popconfirm>
                        )}
                    </Space>
                </Space>
            )}
        </Card>
    );
}

export default NodeBranding;
